import { useTheme } from '@/src/context/ThemeContext';
import { UseCaseLandingConfig, UseCaseLandingScreen } from '@/src/presentation/screens/UseCaseLandingScreen';
import Head from 'expo-router/head';
import { useRouter } from 'expo-router';

const PAGE_URL = 'https://tournaly.app/round-robin-tournament';
const OG_IMAGE = 'https://tournaly.app/og-image.png';

const CONFIG: UseCaseLandingConfig = {
  badge: 'Round Robin',
  badgeEs: 'Round Robin',
  badgeIcon: 'repeat-outline',
  title: 'Round Robin',
  titleEs: 'Torneos de',
  titleAccent: 'Tournament Maker',
  titleAccentEs: 'Round Robin',
  subtitle:
    'Everyone plays everyone. Tournaly auto-schedules every matchup, tracks wins, draws and losses in real time, and generates a clean standings table.',
  subtitleEs:
    'Todos juegan contra todos. Tournaly programa automáticamente cada enfrentamiento, registra victorias, empates y derrotas en tiempo real y genera una clasificación limpia.',
  steps: [
    {
      title: 'Add all participants',
      titleEs: 'Añade todos los participantes',
      desc: 'Add players or teams manually, invite by email or share the invite code.',
      descEs: 'Añade jugadores o equipos manualmente, invita por email o comparte el código de invitación.',
    },
    {
      title: 'Every matchup is auto-scheduled',
      titleEs: 'Todos los enfrentamientos se programan automáticamente',
      desc: 'Tournaly calculates all the round-robin pairs — every participant plays every other participant exactly once.',
      descEs: 'Tournaly calcula todos los pares — cada participante juega contra los demás exactamente una vez.',
    },
    {
      title: 'Track results & standings',
      titleEs: 'Registra resultados y clasificación',
      desc: 'Enter scores after each match. The standings table updates in real time with wins, draws, losses and points.',
      descEs: 'Introduce puntuaciones tras cada partido. La clasificación se actualiza en tiempo real con victorias, empates, derrotas y puntos.',
    },
  ],
  features: [
    {
      icon: 'calendar-outline',
      color: '#FF6B35',
      title: 'Auto-Scheduled Fixtures',
      titleEs: 'Calendario Automático',
      desc: 'All matchups calculated and ordered automatically — no manual scheduling.',
      descEs: 'Todos los enfrentamientos calculados y ordenados automáticamente — sin programación manual.',
    },
    {
      icon: 'bar-chart-outline',
      color: '#45B7D1',
      title: 'Live Standings Table',
      titleEs: 'Tabla de Clasificación en Vivo',
      desc: 'Wins, draws, losses, points and goal difference — updated after every result.',
      descEs: 'Victorias, empates, derrotas, puntos y diferencia de goles — actualizados tras cada resultado.',
    },
    {
      icon: 'podium-outline',
      color: '#4ECDC4',
      title: 'Tiebreaker Support',
      titleEs: 'Soporte de Desempate',
      desc: 'Custom score entry supports win/draw/loss tracking with automatic point calculation.',
      descEs: 'La entrada de puntuaciones registra victoria/empate/derrota con cálculo automático de puntos.',
    },
    {
      icon: 'share-social-outline',
      color: '#06B6D4',
      title: 'Export PNG & PDF',
      titleEs: 'Exportar PNG y PDF',
      desc: 'Share the full standings as an image or print the results table as a PDF.',
      descEs: 'Comparte la clasificación completa como imagen o imprime la tabla de resultados como PDF.',
    },
    {
      icon: 'star-outline',
      color: '#FFD700',
      title: 'League Integration',
      titleEs: 'Integración con Ligas',
      desc: 'Combine multiple round-robin tournaments into a league with cumulative point standings.',
      descEs: 'Combina varios torneos round robin en una liga con clasificación de puntos acumulada.',
    },
    {
      icon: 'phone-portrait-outline',
      color: '#A78BFA',
      title: 'Mobile Apps',
      titleEs: 'Apps Móviles',
      desc: 'Update results on the go from the iOS or Android app.',
      descEs: 'Actualiza resultados desde la app de iOS o Android.',
    },
  ],
  relatedBlogSlug: '2026-03-06-round-robin-tournament-guide',
  relatedBlogTitle: 'Round Robin Tournament Guide — Rules, Format & Strategy',
  relatedBlogTitleEs: 'Guía de Torneos Round Robin — Reglas, Formato y Estrategia',
  relatedBlogSummary: 'Everything you need to know about the round-robin format: scheduling, standings, tiebreakers and when to use it.',
  relatedBlogSummaryEs: 'Todo lo que necesitas saber sobre el formato round robin: calendario, clasificación, desempates y cuándo usarlo.',
};

const title = 'Round Robin Tournament Maker — Free Online Tool | Tournaly';
const description =
  'Create and manage round robin tournaments online for free. Auto-schedule all matchups, track live standings, export results. Up to 16 players free.';

export default function RoundRobinTournamentPage() {
  const { colors, isDark, toggleTheme } = useTheme();
  const router = useRouter();

  const jsonLd = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'Tournaly — Round Robin Tournament Maker',
    applicationCategory: 'SportsApplication',
    operatingSystem: 'Web, iOS, Android',
    url: PAGE_URL,
    description,
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    publisher: { '@type': 'Organization', name: 'Tournaly', url: 'https://tournaly.app' },
  });

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={PAGE_URL} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Tournaly" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={PAGE_URL} />
        <meta property="og:image" content={OG_IMAGE} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={OG_IMAGE} />
        <meta name="keywords" content="round robin tournament, round robin bracket, round robin schedule maker, round robin generator, tournament maker" />
        <script type="application/ld+json">{jsonLd}</script>
      </Head>
      <UseCaseLandingScreen
        config={CONFIG}
        isDark={isDark}
        onToggleTheme={toggleTheme}
        onGetStarted={() => router.push('/auth/register' as any)}
        onSignIn={() => router.push('/auth/login' as any)}
        onBlogPostPress={(slug) => {
          if (slug === 'blog') router.push('/blog' as any);
          else if (slug === 'privacy') router.push('/privacy' as any);
          else if (slug === 'terms') router.push('/terms' as any);
          else router.push(`/blog/${slug}` as any);
        }}
        colors={colors}
      />
    </>
  );
}
